// =====================================================================
// src/components/intl-acca/IntlAccaLegRow.tsx
// =====================================================================
// Single-leg row inside a Slip card. Pure presentational — props are the
// exact IntlAccaSlipLeg row shape from src/db/intl-acca-types.ts.
//
// Empty-by-design: closing_price + clv_pp + result are nullable until
// real Pinnacle / settlement flows. This component MUST render those
// null paths explicitly — it never reconstructs CLV from market price
// alone (PRD §12).
//
// Variants:
//   - "default"        full leg row (reasoning visible, pricing audit, CLV)
//   - "compact"        single-line for archive / history lists
//   - "teaser-blurred" rendered behind <TierLockedTeaser> — reasoning_note
//                      stays visible per PRD §12 "never blank" rule, but
//                      the parent overlay handles the blur.

import type { IntlAccaSlipLeg } from "../../db/intl-acca-types";
import { formatOdds, legStage } from "../../db/intl-acca-types";
import { CLVConfidencePill } from "./CLVConfidencePill";

export interface IntlAccaLegRowProps {
  /** 1-based display index — derived in the parent; not stored on the leg. */
  index: number;
  leg: IntlAccaSlipLeg;
  variant?: "default" | "compact";
  /** Optional human-readable match label — host resolves from match_id. */
  matchLabel?: string;
}

export function IntlAccaLegRow({
  index,
  leg,
  variant = "default",
  matchLabel,
}: IntlAccaLegRowProps): JSX.Element {
  const stage = legStage(leg);

  if (variant === "compact") {
    return (
      <div
        className="ia-leg-row"
        style={{ gridTemplateColumns: "36px 1fr 80px 60px" }}
      >
        <span className="ia-leg-index">L{String(index).padStart(2, "0")}</span>
        <div>
          <div>
            <span className="ia-leg-selection">{leg.selection}</span>
            {matchLabel ? (
              <span className="ia-leg-market"> · {matchLabel}</span>
            ) : null}
          </div>
        </div>
        <span className="ia-mono" style={{ textAlign: "right", color: "var(--text)" }}>
          {formatOdds(leg.market_price_at_selection)}
        </span>
        <CLVConfidencePill confidence={leg.clv_confidence} clv_pp={leg.clv_pp} hideValue />
      </div>
    );
  }

  return (
    <div className="ia-leg-row">
      {/* Index */}
      <div>
        <div className="ia-leg-index">L{String(index).padStart(2, "0")}</div>
      </div>

      {/* Body: selection + market label + write-once reasoning note */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="ia-leg-selection">{leg.selection}</span>
          {matchLabel ? (
            <span className="ia-leg-market" style={{ fontSize: "var(--sz-body-sm)" }}>
              · {matchLabel}
            </span>
          ) : (
            <span className="ia-leg-market ia-mono" style={{ fontSize: "var(--sz-mono-xs)" }}>
              · {leg.match_id}
            </span>
          )}
          {leg.result ? (
            <span
              className={
                leg.result === "WON"
                  ? "ia-pill ia-pill-pos"
                  : leg.result === "LOST"
                    ? "ia-pill ia-pill-neg"
                    : "ia-pill ia-pill-ghost"
              }
            >
              {leg.result}
            </span>
          ) : null}
        </div>
        {/* PRD §12 hard constraint: reasoning_note is NOT NULL and never hidden. */}
        <p className="ia-leg-reasoning">{leg.reasoning_note}</p>
      </div>

      {/* Pricing audit — strikza_price + market_price are NOT NULL at the DB.
          closing_price is nullable: show "—" when null (no Pinnacle yet). */}
      <div>
        <div className="ia-pricing">
          <span className="ia-pricing-strikza">{formatOdds(leg.strikza_price)}</span>
          <span className="ia-pricing-market">/ {formatOdds(leg.market_price_at_selection)} mkt</span>
        </div>
        <div
          className="ia-mono"
          style={{
            marginTop: 4,
            fontSize: "var(--sz-mono-xs)",
            color: "var(--text-tertiary)",
            textAlign: "right",
            letterSpacing: "0.16em",
          }}
        >
          {leg.closing_price === null ? (
            <span>CLOSE — AWAIT PINNACLE</span>
          ) : (
            <>CLOSE {formatOdds(leg.closing_price)}</>
          )}
        </div>
      </div>

      {/* CLV cell — honest about the unsettled state */}
      <div style={{ textAlign: "right" }}>
        <CLVConfidencePill confidence={leg.clv_confidence} clv_pp={leg.clv_pp} />
        {stage === "unsettled" ? (
          <div className="ia-clv-cell-empty" style={{ marginTop: 6 }}>
            Awaiting close
          </div>
        ) : null}
      </div>
    </div>
  );
}
